import styled from '@emotion/styled';
import FormHelperText from '@mui/material/FormHelperText';
import React, {useCallback} from 'react';

import {mixinFlexboxRow} from '@ELib/styles/mixins';
import {classyArray} from '@ELib/utilities/HelperFunctions';

import {ECancelButton, ESubmitButton} from '../EButtons';

export const EForm = ({
  onSubmit,
  children,
  ...htmlProps
}: {
  onSubmit: () => void;
  children: React.ReactNode | React.ReactNode[];
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLFormElement>,
  HTMLFormElement
>): React.ReactElement => {
  const handleSubmit = useCallback(
    (evt: React.FormEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onSubmit();
      return false;
    },
    [onSubmit]
  );
  return (
    <StyledForm {...htmlProps} onSubmit={handleSubmit}>
      {children}
    </StyledForm>
  );
};

export const EFormButtonRow = styled(
  (
    props: React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >
  ) => (
    <div
      {...props}
      className={classyArray(['EFormButtonRow', props.className])}
    />
  )
)`
  ${mixinFlexboxRow}
  span {
    align-self: center;
  }
`;

export const ECancelSubmitButtonRow = ({
  onCancel,
  isSubmitting = false,
  submitButtonDisabled = false,
  submitButtonText,
  submitButtonTestId,
  danger = false,
}: {
  onCancel?: () => void;
  isSubmitting: boolean;
  submitButtonDisabled?: boolean;
  submitButtonText?: string;
  submitButtonTestId?: string;
  danger?: boolean;
}): React.ReactElement => (
  <EFormButtonRow>
    {onCancel ? (
      <ECancelButton disabled={isSubmitting} onClick={onCancel} />
    ) : null}
    <ESubmitButton
      disabled={isSubmitting || submitButtonDisabled}
      isSubmitting={isSubmitting}
      testId={submitButtonTestId}
      variant={danger ? 'danger' : undefined}
    >
      {submitButtonText}
    </ESubmitButton>
  </EFormButtonRow>
);

const StyledForm = styled.form`
  display: flex;
  gap: var(--gutter-small);
  flex-direction: column;

  & .EFormButtonRow {
    margin-top: var(--gutter);
  }
`;

export const EFormErrorText = ({
  errorMessage,
}: {
  errorMessage: string;
}): React.ReactElement => <FormHelperText error>{errorMessage}</FormHelperText>;
